
import React, { useState, useRef, useEffect } from "react";
import { AtSign, PhoneCall, Mail, Send } from "lucide-react";
import { Textarea } from "./ui/textarea";
import { Button } from "./ui/button";
import { CommentIcon } from "../types/prospect";
import { useOnClickOutside } from "../hooks/useOnClickOutside";

interface CommentEditorProps {
  onSave: (text: string, mentions?: string[], icon?: CommentIcon) => void;
  relationshipManagers?: any[];
}

const CommentEditor: React.FC<CommentEditorProps> = ({
  onSave,
  relationshipManagers = [],
}) => {
  const [text, setText] = useState("");
  const [showMentions, setShowMentions] = useState(false);
  const [mentionFilter, setMentionFilter] = useState("");
  const [mentions, setMentions] = useState<string[]>([]);
  const [currentIcon, setCurrentIcon] = useState<CommentIcon>(null);
  const [cursorPosition, setCursorPosition] = useState<number | null>(null);
  
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const mentionsRef = useRef<HTMLDivElement>(null);
  const editorRef = useRef<HTMLDivElement>(null);
  
  // Add outside click handler
  useOnClickOutside(mentionsRef, (event) => {
    // Only close if the click is outside both the mentions dropdown and the textarea
    if (textareaRef.current && !textareaRef.current.contains(event.target as Node)) {
      setShowMentions(false);
    }
  });

  useEffect(() => {
    // Focus on textarea when component mounts
    if (textareaRef.current) {
      textareaRef.current.focus();
    }
  }, []);

  const handleTextChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const newText = e.target.value;
    setText(newText);
    
    // Check for @ symbol to show mentions dropdown
    const cursorPos = e.target.selectionStart;
    setCursorPosition(cursorPos);
    
    const textBeforeCursor = newText.substring(0, cursorPos);
    const lastAtSymbolIndex = textBeforeCursor.lastIndexOf('@');
    
    if (lastAtSymbolIndex >= 0 && !newText.substring(lastAtSymbolIndex, cursorPos).includes(' ')) {
      setShowMentions(true);
      setMentionFilter(textBeforeCursor.substring(lastAtSymbolIndex + 1));
    } else {
      setShowMentions(false);
      setMentionFilter("");
    }
  };

  const handleSelectMention = (rm: any) => {
    if (!cursorPosition) return;
    
    const textBeforeCursor = text.substring(0, cursorPosition);
    const textAfterCursor = text.substring(cursorPosition);
    
    const lastAtSymbolIndex = textBeforeCursor.lastIndexOf('@');
    
    if (lastAtSymbolIndex >= 0) {
      const newText = 
        textBeforeCursor.substring(0, lastAtSymbolIndex) + 
        '@' + rm.name + ' ' + 
        textAfterCursor;
      
      setText(newText);
      setMentions([...mentions, rm.id]);
      setShowMentions(false);
      
      // Focus back on textarea
      if (textareaRef.current) {
        const newCursorPos = lastAtSymbolIndex + rm.name.length + 2; // +2 for @ and space
        setTimeout(() => {
          textareaRef.current!.focus();
          textareaRef.current!.setSelectionRange(newCursorPos, newCursorPos);
        }, 0);
      }
    }
  };

  const addIcon = (icon: CommentIcon) => {
    // Set the current icon and clear any previous one
    setCurrentIcon(currentIcon === icon ? null : icon);
  };

  const handleSaveComment = () => {
    if (text.trim()) {
      onSave(text.trim(), mentions.length > 0 ? mentions : undefined, currentIcon);
      
      // Reset the form
      setText("");
      setMentions([]);
      setCurrentIcon(null);
    }
  };

  const getInitials = (name: string) => {
    return name.split(" ")
      .map(part => part[0])
      .join("")
      .toUpperCase()
      .substring(0, 2);
  };

  const filteredManagers = relationshipManagers
    .filter(rm => rm.name.toLowerCase().includes(mentionFilter.toLowerCase()));

  return (
    <div className="space-y-3" ref={editorRef}>
      <div className="relative">
        <Textarea
          ref={textareaRef}
          value={text}
          onChange={handleTextChange}
          placeholder="Add a Conversation..."
          className="w-full min-h-24 p-3 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
        />
        {showMentions && filteredManagers.length > 0 && (
          <div 
            ref={mentionsRef}
            className="absolute left-0 bg-white border rounded-md shadow-lg z-50 max-h-40 overflow-y-auto w-48"
            style={{ bottom: '52px' }}
          >
            <div className="sticky top-0 bg-white border-b p-2">
              <input
                type="text"
                placeholder="Search users..."
                value={mentionFilter}
                onChange={(e) => setMentionFilter(e.target.value)}
                className="w-full p-1 text-sm border rounded"
                onClick={(e) => e.stopPropagation()} // Prevent click from closing dropdown
              />
            </div>
            {filteredManagers.map((rm, index) => (
              <button
                key={index}
                className="w-full text-left px-3 py-2 hover:bg-gray-100 text-sm flex items-center"
                onClick={() => handleSelectMention(rm)}
              >
                <span className="w-6 h-6 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center mr-2 flex-shrink-0 text-xs">
                  {getInitials(rm.name)}
                </span>
                {rm.name}
              </button>
            ))}
          </div>
        )}
      </div>
      <div className="flex items-center justify-between px-2 py-1">
        <div className="flex items-center gap-2">
          <button
            onClick={() => addIcon('phone')}
            className={`p-1.5 rounded-full border ${currentIcon === 'phone' ? 'bg-blue-100 border-blue-500' : 'hover:bg-gray-100'}`}
            title="Add phone call note"
            aria-label="Phone call"
            type="button"
          >
            <PhoneCall className="w-4 h-4" />
          </button>
          <button
            onClick={() => addIcon('email')}
            className={`p-1.5 rounded-full border ${currentIcon === 'email' ? 'bg-blue-100 border-blue-500' : 'hover:bg-gray-100'}`}
            title="Add email note"
            aria-label="Email"
            type="button"
          >
            <Mail className="w-4 h-4" />
          </button>
          <div className="text-xs text-gray-500">
            Type @ to mention someone
          </div>
        </div>
        
        <Button 
          onClick={handleSaveComment}
          disabled={!text.trim()}
          size="sm"
          className="flex items-center gap-1"
        >
          <Send className="w-4 h-4" /> Save
        </Button>
      </div>
    </div>
  );
};

export default CommentEditor;
